import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  User,
  Clock,
  Globe,
  Monitor,
  ExternalLink,
  Copy,
  Check
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { AuditLog, ACTIVITY_TYPE_CONFIGS } from '@/types/auditLog';
import { ChangeViewer } from './ChangeViewer';

interface AuditLogDetailProps {
  log: AuditLog | null;
  isOpen: boolean;
  onClose: () => void;
  isLoading?: boolean;
}

const formatDateTime = (dateString: string): string => {
  return new Date(dateString).toLocaleString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
  });
};

export const AuditLogDetail: React.FC<AuditLogDetailProps> = ({
  log,
  isOpen,
  onClose,
  isLoading = false,
}) => {
  const [copied, setCopied] = React.useState(false);

  const handleCopyId = async () => {
    if (log?.id) {
      await navigator.clipboard.writeText(log.id);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const config = log ? ACTIVITY_TYPE_CONFIGS[log.type] || ACTIVITY_TYPE_CONFIGS.DEFAULT : ACTIVITY_TYPE_CONFIGS.DEFAULT;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black bg-opacity-50 z-40"
          />

          {/* Panel */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="fixed right-0 top-0 bottom-0 w-full max-w-lg bg-white shadow-xl z-50 flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                Audit Log Details
              </h2>
              <Button variant="ghost" size="sm" onClick={onClose}>
                <X className="w-5 h-5" />
              </Button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4">
              {isLoading ? (
                <div className="space-y-4 animate-pulse">
                  <div className="h-8 bg-gray-200 rounded w-3/4" />
                  <div className="h-4 bg-gray-200 rounded w-1/2" />
                  <div className="h-32 bg-gray-200 rounded" />
                </div>
              ) : log ? (
                <div className="space-y-6">
                  {/* Activity Type Badge */}
                  <div className={cn('inline-flex items-center gap-2 px-3 py-2 rounded-lg', config.bgColor)}>
                    <span className={cn('text-sm font-medium', config.color)}>
                      {config.label}
                    </span>
                  </div>

                  {/* Description */}
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-1">Description</h3>
                    <p className="text-base text-gray-900">{log.description}</p>
                  </div>

                  {/* User Info */}
                  {log.user && (
                    <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                        <User className="w-4 h-4 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{log.user.name}</p>
                        <p className="text-xs text-gray-500">{log.user.email}</p>
                      </div>
                    </div>
                  )}

                  {/* Timestamp */}
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Clock className="w-4 h-4 text-gray-400" />
                    {formatDateTime(log.createdAt)}
                  </div>

                  {/* Entity Reference */}
                  {log.entityType && log.entityId && (
                    <div>
                      <h3 className="text-sm font-medium text-gray-500 mb-2">Related Entity</h3>
                      <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                        <span className="text-sm text-gray-600">
                          {log.entityType}: <span className="font-mono text-xs">{log.entityId}</span>
                        </span>
                        <ExternalLink className="w-4 h-4 text-blue-500" />
                      </div>
                    </div>
                  )}

                  {/* Changes */}
                  {log.changes && Object.keys(log.changes).length > 0 && (
                    <div>
                      <h3 className="text-sm font-medium text-gray-500 mb-2">Changes</h3>
                      <ChangeViewer changes={log.changes} />
                    </div>
                  )}

                  {/* Metadata */}
                  {log.metadata && Object.keys(log.metadata).length > 0 && (
                    <div>
                      <h3 className="text-sm font-medium text-gray-500 mb-2">Additional Info</h3>
                      <pre className="text-xs bg-gray-50 p-3 rounded-lg overflow-x-auto">
                        {JSON.stringify(log.metadata, null, 2)}
                      </pre>
                    </div>
                  )}

                  {/* Technical Info */}
                  <div className="pt-4 border-t border-gray-200">
                    <h3 className="text-sm font-medium text-gray-500 mb-3">Technical Details</h3>

                    {/* Log ID */}
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs text-gray-500">Log ID</span>
                      <div className="flex items-center gap-1">
                        <code className="text-xs font-mono bg-gray-100 px-2 py-1 rounded">
                          {log.id}
                        </code>
                        <button
                          onClick={handleCopyId}
                          className="p-1 hover:bg-gray-100 rounded"
                          title="Copy ID"
                        >
                          {copied ? (
                            <Check className="w-3 h-3 text-green-500" />
                          ) : (
                            <Copy className="w-3 h-3 text-gray-400" />
                          )}
                        </button>
                      </div>
                    </div>

                    {/* IP Address */}
                    {log.ipAddress && (
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs text-gray-500 flex items-center gap-1">
                          <Globe className="w-3 h-3" /> IP Address
                        </span>
                        <code className="text-xs font-mono text-gray-600">{log.ipAddress}</code>
                      </div>
                    )}

                    {/* User Agent */}
                    {log.userAgent && (
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500 flex items-center gap-1">
                          <Monitor className="w-3 h-3" /> User Agent
                        </span>
                        <code className="text-xs font-mono text-gray-600 truncate max-w-[200px]">
                          {log.userAgent}
                        </code>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <p className="text-gray-500">No log selected</p>
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
